import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, Pipe, PipeTransform, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroupDirective,
  NgForm,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatAutocomplete } from "@angular/material/autocomplete";
import { ErrorStateMatcher } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSelectChange } from "@angular/material/select";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  CustomErrorStateMatcher,
  PhoneValidator,
  PREVENTCHARACTER,
  PreventCharacter,
} from "@comunicare/comunicare-angular-forms-validator";
import { ConfirmationDialogComponent, ConfirmationDialogType } from "@comunicare/comunicare-dashboard-modals";
import { ArrayHelper, FileLogger, Tools } from "@comunicare/comunicare-helper";
import {
  CONTROL_KEYS,
  ERROR_MSG,
  FHIRHelper,
  IAddPatientData,
  IAddPatientParameter,
  IAddress,
  IAppError,
  ICoding,
  ICountry,
  IDENTITY_STATUS,
  IDENTITY_VERIFICATION,
  IDENTITY_VERIFICATION_METHOD,
  IHistoriqueIns,
  IIdentifier,
  IIdentity,
  IIdentityVerificationMethods,
  INSIReturnCode,
  InsResponse,
  IPatient,
  IPatientUser,
  IReference,
  MODIFICATION_TYPE,
  Organization,
  Practitioner,
  PreferenceContext,
  QUALIFICATIONS,
  Reference,
  SYSTEM_COMUNICARE,
  USER_STATUS,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { forkJoin, Observable, of, Subject, Subscription } from "rxjs";
import { concatMap, finalize, first, map, skipWhile, switchMap, takeUntil, tap } from "rxjs/operators";
import { FormsData } from "src/app/helpers/formsData";
import { insType, MatriculeInsHelper } from "src/app/helpers/matriculeInsHelper";
import { PatientHelper } from "src/app/helpers/patientHelper";
import { ConfigService } from "src/app/providers/config.service";
import { CountriesService } from "src/app/providers/countries.service";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { INSService } from "src/app/providers/ins.service";
import { OrganizationsService } from "src/app/providers/organizations.service";
import { PatientService } from "src/app/providers/patient.service";
import { PatientRegistrationService } from "src/app/providers/patientRegistration.service";
import { PractitionerService } from "src/app/providers/practitioner.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { SessionService } from "src/app/providers/session.service";
import { TownsService } from "src/app/providers/towns.service";
import { UserService } from "src/app/providers/user.service";
import { UserStatisticsService } from "src/app/providers/userStatistics.service";
import { PatientAlreadyExistsDialogComponent } from "../forms/add-patient/patient-already-exists-dialog/patient-already-exists-dialog.component";
import { TownServerSideSearchComponent } from "../forms/add-patient/town-server-side-search-component/town-server-side-search.component";

export class AtLeastOneErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const practitioners = form.form.controls.practitioners;
    const isSubmitted = form && form.submitted;
    return !!(control && practitioners.invalid && (practitioners.touched || isSubmitted));
  }
}

@Pipe({ name: "getPractitionerName" })
export class GetPractitionerNamePipe implements PipeTransform {
  transform(practitioner: Practitioner, practitionerService: PractitionerService): string {
    return practitionerService.getPractitionerName(practitioner);
  }
}
@Pipe({ name: "getIdentityVerificationMethodDisplay" })
export class getIdentityVerificationMethodDisplay implements PipeTransform {
  transform(
    i: number,
    serviceIdentityVerifMethod: IIdentityVerificationMethods[],
    lang: string,
    translateService: TranslateService
  ): string {
    if (serviceIdentityVerifMethod?.length) {
      return serviceIdentityVerifMethod.find((s) => s.term === i.toString())[lang];
    }
    return translateService.instant("forms.identityVerificationMethod." + IDENTITY_VERIFICATION_METHOD[i]);
  }
}

@Pipe({ name: "displayNoSmsCheckbox", pure: false })
export class DisplayNoSmsCheckbox implements PipeTransform {
  transform(isCreation: boolean, patientForm: UntypedFormGroup): boolean {
    if (!isCreation) {
      return false;
    }
    return Tools.isDefined(patientForm.get("phone").value) && patientForm.get("phone").value !== "";
  }
}

@Component({
  selector: "app-patient-form",
  templateUrl: "./patient-form.component.html",
  styleUrls: ["./patient-form.component.scss"],
})
export class PatientFormComponent implements OnInit, OnDestroy {
  @ViewChild(TownServerSideSearchComponent) townServerSideSearch: TownServerSideSearchComponent;
  public FRANCE: string;
  public IDENTITY_VERIFICATION = IDENTITY_VERIFICATION;
  public IDENTITY_VERIFICATION_METHOD: number[];
  public IDENTITY_STATUS = IDENTITY_STATUS;
  public PREVENTCHARACTER = PREVENTCHARACTER;
  public USER_STATUS = USER_STATUS;
  public allEthnicGroups = [];
  @ViewChild("insInput") insInput: ElementRef;
  @ViewChild("auto") matAutocomplete: MatAutocomplete;
  private $insuOrganizations = this.organisationService.listSome("ins", this.sessionService.currentCountry);
  private $allPractitioners: Observable<Practitioner[]>;
  private $preferences: Observable<IAddPatientParameter> = this.preferenceService.list(PreferenceContext.ADD_PATIENT_FORM);
  public isLoading = true;
  public departments: Reference[];
  public monitoringPractitioners: Practitioner[];
  public availableOrganizations: Organization[];
  public availableDoctors: Practitioner[];
  public availableNurses: Practitioner[];
  public availableSecretaries: Practitioner[];
  public availablePharmacist: Practitioner[];
  public availableInsurances: Organization[];
  public filteredInsurances: Organization[];
  public availableLangs: ICoding[];
  public availableGender: ICoding[];
  public allPractitioners: Practitioner[];
  public maxDate: moment.Moment = moment();
  public patientForm: UntypedFormGroup;
  public matcher = new CustomErrorStateMatcher();
  public atLeastOneMatcher = new AtLeastOneErrorStateMatcher();
  public noSms = false;
  public userTest = false;
  public goToCp = true;
  public isAnonymous = false;
  public selectedDepartment: Reference = undefined;
  public selectedOrg: Reference = undefined;
  public submitted = false;
  public startFadeOut = false;
  public emailAlreadyTaken = false; // variable to stock response from put /patient
  public monitoringOrgsRefs: Reference[] = [];
  public monitoringServicesRefs: Reference[] = [];
  private monitServicesLinks: Map<string, Reference[]> = new Map<string, Reference[]>();
  private monitoringPractitionersByOrg: Map<string, Set<string>> = new Map<string, Set<string>>();
  public isAllServices = false;
  public isAllOrg = false;
  public monitoringOrgLocked = false;
  public monitoringServiceLocked = false;
  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();
  private monitLinkSub$: Subscription;
  private monitServLinkSub$: Subscription;
  public saveInProgress = false;
  public showMore = false;
  public country: string;
  private selectedDepartmentCountry: string;
  public showMoreNames = false;
  public identityStatus = IDENTITY_STATUS.temporary;
  public identityFromINSI = false;
  private newIdentities: IIdentity[] = [];
  public isSearching4InsiPatient = false;
  public searchIsLoading = false;
  public foundInsPatient: InsResponse;
  public INSIReturnCode = INSIReturnCode;
  public notUsableInsPatient = false;
  public canUpdateRetrievedIdentityPermission = false;
  public canUseINSiService = false;
  public serviceIdentityVerifMethod: IIdentityVerificationMethods[];
  public insHistory: IHistoriqueIns[];
  public showInsHistory = false;
  public insType = insType;
  public searchFinished: boolean;
  public nameError: string;
  public firstnamesError: string;
  public firstnameError: string;
  public birthplaceRequired: boolean;
  public showStatusHelp = false;
  public isMinor = false;
  public addRelated = false;
  public availableCountries: ICountry[];
  public phoneNotRequired = false;
  public selectReady = true;
  public careplanRequiredForMonitoringEmail = false;
  public careplanRequiredForSignup = false;
  public noteMaxLength = 100;

  @Input() isCreation: boolean;
  @Input() patient: IPatientUser;
  @Input() source: string;
  @Input() isFromRouting = true;
  @Input() linkData: {
    name: string;
    value: string | number;
  }[];

  constructor(
    private userService: UserService,
    private fb: UntypedFormBuilder,
    public formsData: FormsData,
    public practitionerService: PractitionerService,
    private organisationService: OrganizationsService,
    private patientService: PatientService,
    private preferenceService: PreferenceService,
    public sessionService: SessionService,
    private healthcareService: HealthcareserviceService,
    public translateService: TranslateService,
    private snackBar: MatSnackBar,
    private insService: INSService,
    private cdr: ChangeDetectorRef,
    private townsService: TownsService,
    private statService: UserStatisticsService,
    private countriesService: CountriesService,
    private dialog: MatDialog,
    public patientRegistrationService: PatientRegistrationService,
    private configService: ConfigService
  ) {
    if (this.isFromRouting) {
      this.isCreation = this.patientRegistrationService.isCreation;
      this.source = this.patientRegistrationService.source;
      this.linkData = this.patientRegistrationService.linkData;
    }

    this.userService.getAllEthnicGroups().then((groups) => {
      this.allEthnicGroups = groups;
    });
    this.countriesService
      .list()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((countries) => {
        this.availableCountries = countries;
      });
  }

  ngOnInit(): void {
    this.translateService.onLangChange.pipe(takeUntil(this.onDestroy$)).subscribe(async () => {
      this.selectReady = false;
      setTimeout(() => (this.selectReady = true));
      this.availableGender = this.formsData.GENDER;
      this.availableLangs = await this.formsData.getLanguages();
    });
    this.setupForm();
    this.preferenceService
      .getGlobal()
      .pipe(first())
      .subscribe((pref) => {
        if (pref) {
          this.patientRegistrationService.go2patientPage = pref.go2PatientAfterInscription;
        }
      });

    if (
      this.sessionService.currentService &&
      this.userService.ownOrganization &&
      (this.healthcareService.availableServices()?.length > 0 || this.healthcareService.availableMonitoringServices().length > 0)
    ) {
      this.setupServicesWatch();
    } else {
      this.sessionService.servicesSetupWatch
        .pipe(
          skipWhile(() => !this.userService.ownOrganization),
          first()
        )
        .subscribe(() => {
          this.setupServicesWatch();
        });
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private setupForm(valueForm?: any) {
    this.isAnonymous = this.patient?.user?.anonymous || false;
    this.country = this.isCreation
      ? this.sessionService.currentCountry
      : this.patient?.user?.referenceCountry // when updating a patient file, the patient's country must be taken into account -> important for insi, mutuelle, etc.
      ? this.patient?.user?.referenceCountry
      : this.sessionService.currentCountry; // backup
    this.FRANCE = this.sessionService.FRANCE;
    this.checkUsersPermissions();

    this.patientForm = this.fb.group({
      healthcareService: new UntypedFormControl(valueForm?.healthcareService ?? undefined, this.isCreation ? Validators.required : null),
      organization: new UntypedFormControl(valueForm?.organization ?? undefined, this.isCreation ? Validators.required : null),
      name: new UntypedFormControl(
        {
          value: valueForm?.name ? (this.country === this.FRANCE ? valueForm.name.toUpperCase() : valueForm.name) : undefined,
          disabled: this.isAnonymous ? true : false,
        },
        {
          validators:
            this.country === this.FRANCE
              ? [Validators.required, Validators.pattern("[A-Za-z- ']*"), this.NameValidator]
              : [Validators.required, Validators.pattern("[^0-9]*")],
        }
      ),
      firstname: new UntypedFormControl(
        {
          value: valueForm?.firstname
            ? this.country === this.FRANCE
              ? valueForm.firstname.toUpperCase()
              : valueForm.firstname
            : undefined,
          disabled: this.isAnonymous ? true : false,
        },
        [
          this.country === this.FRANCE ? Validators.pattern("[A-Za-z- ']*") : Validators.required,
          this.firstnameValidator,
          this.FirstNamesValidator,
          Validators.pattern("[^0-9]*"),
        ]
      ),
      pseudoname: new UntypedFormControl(valueForm?.pseudoname ?? undefined),
      birthDate: new UntypedFormControl(valueForm?.birthDate ?? undefined, Validators.required),
      phone: new UntypedFormControl(
        { value: valueForm?.phone ?? undefined, disabled: this.isAnonymous ? true : false },
        {
          validators:
            this.addRelated || (this.isMinor && !this.isCreation)
              ? [this.phoneValidator, Validators.pattern("[0-9+]*")]
              : [Validators.required, this.phoneValidator, Validators.pattern("[0-9+]*")],
        }
      ),
      mail: new UntypedFormControl(
        { value: valueForm?.mail ?? undefined, disabled: this.isAnonymous ? true : false },
        { validators: this.addRelated ? [Validators.email] : [Validators.required, Validators.email] }
      ),
      userLang: new UntypedFormControl(
        { value: valueForm?.userLang ?? undefined, disabled: this.isAnonymous ? true : false },
        Validators.required
      ),
      gender: new UntypedFormControl(valueForm?.gender ?? undefined, Validators.required),
      practitioners: this.fb.group(
        {
          secretary: new UntypedFormControl({
            value: valueForm?.practitioners?.secretary ?? undefined,
            disabled: this.isAnonymous ? true : false,
          }),
          pharmacist: new UntypedFormControl({
            value: valueForm?.practitioners?.pharmacist ?? undefined,
            disabled: this.isAnonymous ? true : false,
          }),
          nurse: new UntypedFormControl({
            value: valueForm?.practitioners?.nurse ?? undefined,
            disabled: this.isAnonymous ? true : false,
          }),
          doc: new UntypedFormControl({
            value: valueForm?.practitioners?.doc ?? undefined,
            disabled: this.isAnonymous ? true : false,
          }),
        },
        { validators: this.atLeastOneValidator }
      ),
      internalId: new UntypedFormControl(
        { value: valueForm?.internalId ?? undefined, disabled: this.isAnonymous ? true : false },
        { validators: [Validators.minLength(5)] }
      ),
      insurance: new UntypedFormControl({
        value: valueForm?.insurance ?? undefined,
        disabled: this.isAnonymous ? true : false,
      }),
      insuranceId: new UntypedFormControl(
        { value: valueForm?.insuranceId ?? undefined, disabled: this.country !== this.FRANCE ? true : false },
        this.country === this.FRANCE ? MatriculeInsHelper.insValidator : [Validators.pattern("[a-zA-Z0-9]*")]
      ), // when country is not this.FRANCE, the field is diabled until an insurance is selected
      SSIN: new UntypedFormControl(
        {
          value: valueForm?.SSIN ?? undefined,
          disabled: this.isAnonymous ? true : false,
        },
        [Validators.pattern("[a-zA-Z0-9]*")]
      ),
      note: new UntypedFormControl(valueForm?.note ?? undefined, [Validators.maxLength(this.noteMaxLength)]),

      // FR specific fields :
      firstnames: new UntypedFormControl(
        { value: undefined, disabled: this.isAnonymous ? true : false },
        this.country === this.FRANCE ? [Validators.pattern("[A-Za-z-, ']*"), this.FirstNamesValidator] : undefined
      ),
      useName: new UntypedFormControl(undefined, [Validators.pattern("[^0-9]*")]),
      useFirstname: new UntypedFormControl(undefined, [Validators.pattern("[^0-9]*")]),
      birthplace: new UntypedFormControl(undefined, this.country === this.FRANCE ? undefined : undefined),
      identityVerification: new UntypedFormControl(undefined),
      identityVerificationMethod: new UntypedFormControl(undefined),
      // Numéro de sécurité social gérer dans le champ insuranceId
      ins: new UntypedFormControl(undefined, this.insValidator),
      oid: new UntypedFormControl(undefined, this.oidValidator),
      ethnicGroup: new UntypedFormControl(valueForm?.ethnicGroup ?? undefined),
      addressLine1: [valueForm?.addressLine1 ?? ""],
      addressLine2: [valueForm?.addressLine2 ?? undefined],
      addressCP: [valueForm?.addressCP ?? ""],
      addressCity: [valueForm?.addressCity ?? ""],
      addressCountry: [valueForm?.addressCountry ?? ""],
      simulateIns: new FormControl(this.configService.simulateIns),
    });
    if (this.country === this.FRANCE) {
      this.watchFrFields();
    } else {
      this.patientForm
        .get("birthDate")
        ?.valueChanges.pipe(
          takeUntil(this.onDestroy$),
          map((value) => moment().diff(moment(value), "years") < 18),
          tap((isMinor) => (this.isMinor = isMinor)),
          switchMap((isMinor) => {
            if (this.isCreation && isMinor) {
              return this.userService
                .isAuthorized("dashboard/relatedinvite", "POST")
                .pipe(map((isAuth) => ({ isMinor, addRelated: isAuth })));
            }
            return of({ isMinor, addRelated: false });
          })
        )
        .subscribe(({ isMinor, addRelated }) => {
          if (this.isCreation) {
            this.addRelated = addRelated;
            this.patientRegistrationService.onNeedReferentChanged$.next(this.addRelated);
            this.patientForm.get("mail").setValidators(this.addRelated ? [Validators.email] : [Validators.required, Validators.email]);
            this.patientForm
              .get("phone")
              .setValidators(
                this.addRelated
                  ? [this.phoneValidator, Validators.pattern("[0-9+]*")]
                  : [Validators.required, this.phoneValidator, Validators.pattern("[0-9+]*")]
              );
          } else {
            this.patientForm
              .get("phone")
              .setValidators(
                isMinor
                  ? [this.phoneValidator, Validators.pattern("[0-9+]*")]
                  : [Validators.required, this.phoneValidator, Validators.pattern("[0-9+]*")]
              );
          }
        });
    }
    this.patientForm.addControl("monitoringOrgsLink", new UntypedFormControl([]));
    this.setupMonitOrgWatch();
  }

  private setupServicesWatch() {
    this.init();
    if (this.userService.isMonitoringUser) {
      this.sessionService.refreshCurrentMonitService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.init();
        this.setupForm(this.patientForm.getRawValue());
      });
    }
    this.sessionService.refreshCurrentService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.init();
      this.setupForm(this.patientForm.getRawValue());
    });
  }

  private init() {
    this.availableOrganizations = this.organisationService.availableOrganizations();
    const currentOrg = this.sessionService.organization;
    if (currentOrg.reference === this.sessionService.allsOption) {
      this.isAllOrg = true;
      this.selectedOrg = this.availableOrganizations[0].asReference;
      this.patientForm.controls.organization.setValue(this.selectedOrg);
    } else {
      this.isAllOrg = false;
      this.selectedOrg = currentOrg;
      this.patientForm.controls.organization.setValue(this.selectedOrg);
    }
    const current = this.sessionService.currentService;
    if (this.isCreation) {
      if (current?.reference === this.sessionService.allsOption || this.isAllOrg) {
        const services = this.healthcareService.availableServices();
        this.departments = services.map((s) => s.asReference);
        this.selectedDepartment = this.departments[0];
        this.isAllServices = true;
        this.patientForm.controls.healthcareService.setValue(this.selectedDepartment);
        this.setAvailableInsurancesFromSelectedDepartment();
        this.computeFormData();
      } else {
        this.isAllServices = false;
        this.selectedDepartment = current;
        this.patientForm.controls.healthcareService.setValue(this.selectedDepartment);
        this.setAvailableInsurancesFromSelectedDepartment();
        this.computeFormData();
      }
    } else {
      // patient update : use current patient service
      // check if the current selected service is one the patient is in:
      this.selectedOrg = this.patient.patient.managingOrganization;
      const hs = this.patient.patient.healthcareservice.find((s) => s.reference === current?.reference);
      if (hs) {
        // if it is, we select the current one
        this.selectedDepartment = hs;
      } else {
        // if it is not, we try to find which service, available to the user, the patient belong to:
        const availableServices = this.userService.allServices.map((s) => s.serviceRef);
        const serv = this.patient.patient.healthcareservice.find((s) => availableServices.includes(s.reference));
        if (serv) {
          this.selectedDepartment = serv;
        } else {
          // if we do not find any service, we actually have a problem
          // Log it, but still assign a correct service
          FileLogger.error(
            "AddPatientComponent",
            "Could not find patient services in available services list: ",
            this.patient.patient.healthcareservice
          );
          this.selectedDepartment = this.patient.patient.healthcareservice[0];
        }
      }
      this.statService.createStatEvent("Opened edit patient modal", [this.selectedDepartment.reference]);
      this.$insuOrganizations = this.organisationService.listSome("ins", this.patient.user.referenceCountry);
      this.patientForm.get("birthplace").setValue(this.patient.patient.birthplace, { emitEvent: false });
      this.patientForm.controls.healthcareService.setValue(this.selectedDepartment);
      this.computeFormData();
    }
    this.initIdentityVerificationMethodList();
    this.patientForm.controls.insurance.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((value) => {
      if (!value?.organizationName) {
        this.filteredInsurances = this.availableInsurances.filter((ins) => {
          return Tools.suppressDiacritics(ins.organizationName)?.toLowerCase().indexOf(Tools.suppressDiacritics(value).toLowerCase()) > -1;
        });
      }
      this.patientForm.controls.insuranceId.enable();
      if (!value && this.country !== this.FRANCE) {
        this.patientForm.controls.insuranceId.setValue(null);
        this.patientForm.controls.insuranceId.disable();
      }
    });
    if (this.linkData?.length && this.isCreation) {
      this.linkData.forEach((field) => {
        const control = this.patientForm.get(field.name);
        if (control && !control.disabled) {
          control.setValue(field.value, { emitEvent: true });
          control.markAsTouched();
        }
      });
    }
    this.isLoading = false;
  }

  private initIdentityVerificationMethodList(): void {
    this.IDENTITY_VERIFICATION_METHOD = [];
    const service = this.isCreation ? this.selectedDepartment : this.patient.patient.healthcareservice[0];
    this.serviceIdentityVerifMethod = this.userService.allServices.find((s) => s.serviceRef === service?.reference)?.identityVerifMethods;
    if (this.serviceIdentityVerifMethod?.length) {
      this.serviceIdentityVerifMethod.forEach((v, i) => {
        if (v.activated) {
          if (v.term) {
            this.IDENTITY_VERIFICATION_METHOD.push(Number(v.term));
          } else {
            this.IDENTITY_VERIFICATION_METHOD.push(i);
          }
        }
      });
    } else {
      let keys = Object.keys(IDENTITY_VERIFICATION_METHOD);
      keys = keys.slice(0, keys.length / 2);
      this.IDENTITY_VERIFICATION_METHOD = keys.map((key) => Number(key));
    }
  }

  public onSwitchGoToCp(): void {
    this.goToCp = !this.goToCp;
    this.preferenceService
      .update({
        context: PreferenceContext.ADD_PATIENT_FORM,
        parameters: {
          goToCp: this.goToCp,
        },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
    this.patientRegistrationService.onNeedCareplanChanged$.next(this.goToCp);
  }

  private computeIdentityStatus(
    modifType: MODIFICATION_TYPE,
    verificationValue?: IDENTITY_VERIFICATION,
    verificationMethod?: IDENTITY_VERIFICATION_METHOD
  ): void {
    const prevStatus = this.identityStatus;
    const verification = verificationValue !== undefined ? verificationValue : this.patientForm.get("identityVerification").value;
    const methodNum = verificationMethod !== undefined ? verificationMethod : this.patientForm.get("identityVerificationMethod").value;

    let methodLevel = "high";
    if (methodNum && this.serviceIdentityVerifMethod?.length) {
      methodLevel = this.serviceIdentityVerifMethod.find((s) => s.term === methodNum.toString())?.level;
    }
    if (verification === IDENTITY_VERIFICATION.fictive || verification === IDENTITY_VERIFICATION.suspicious) {
      this.identityStatus = IDENTITY_STATUS.temporary;
    } else if (verification === IDENTITY_VERIFICATION.verified && Tools.isDefined(methodNum) && methodLevel === "high") {
      this.identityStatus = this.identityFromINSI ? IDENTITY_STATUS.qualified : IDENTITY_STATUS.validated;
    } else {
      this.identityStatus = this.identityFromINSI ? IDENTITY_STATUS.recovered : IDENTITY_STATUS.temporary;
    }
    if (
      (prevStatus === IDENTITY_STATUS.qualified || prevStatus === IDENTITY_STATUS.recovered) &&
      (this.identityStatus === IDENTITY_STATUS.temporary || this.identityStatus === IDENTITY_STATUS.validated)
    ) {
      // empty ins and oid fields
      this.patientForm.get("oid")?.setValue("", { emitEvent: false });
      this.patientForm.get("ins")?.setValue("", { emitEvent: false });
      this.identityFromINSI = false;
      // unlock fields after retrograding status to temporary
      this.enableFields();
    }
    this.addNewIdentity(modifType);
  }

  private watchFrFields(): void {
    this.patientForm
      .get("identityVerification")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((value: IDENTITY_VERIFICATION) => {
        this.computeIdentityStatus(MODIFICATION_TYPE.verification, value);
      });
    this.patientForm
      .get("identityVerificationMethod")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((value: IDENTITY_VERIFICATION_METHOD) => {
        this.computeIdentityStatus(MODIFICATION_TYPE.method, undefined, value);
      });
    this.patientForm
      .get("ins")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.downgradeIdentityIfNeeded(MODIFICATION_TYPE.ins);
        // The OID is required as soon as an INS is entered: re-evaluate its validity when the INS changes.
        this.patientForm.get("oid")?.updateValueAndValidity({ emitEvent: false });
      });
    this.patientForm
      .get("oid")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.downgradeIdentityIfNeeded(MODIFICATION_TYPE.oid);
        // The INS is required as soon as an OID is entered: re-evaluate its validity when the OID changes.
        this.patientForm.get("ins")?.updateValueAndValidity({ emitEvent: false });
      });
    this.patientForm
      .get("name")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        if (this.patientForm.get("name").errors) {
          this.nameError = Object.keys(this.patientForm.get("name").errors)[0];
        }
        this.downgradeIdentityIfNeeded(MODIFICATION_TYPE.name);
      });
    this.patientForm
      .get("firstnames")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        if (this.patientForm.get("firstnames").errors) {
          this.firstnamesError = Object.keys(this.patientForm.get("firstnames").errors)[0];
        }
        this.downgradeIdentityIfNeeded(MODIFICATION_TYPE.firstnames);
      });
    this.patientForm
      .get("firstname")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        if (this.patientForm.get("firstname").errors) {
          this.firstnameError = Object.keys(this.patientForm.get("firstname").errors)[0];
        }
      });
    this.patientForm
      .get("birthDate")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        this.downgradeIdentityIfNeeded(MODIFICATION_TYPE.birthDate);
        const age = moment().diff(moment(value), "years");
        this.isMinor = age < 18;
        if (this.isCreation) {
          this.addRelated = age < 18;
          this.patientRegistrationService.onNeedReferentChanged$.next(this.addRelated);
          this.patientForm.get("mail").setValidators(this.addRelated ? [Validators.email] : [Validators.required, Validators.email]);
          this.patientForm
            .get("phone")
            .setValidators(
              this.addRelated
                ? [this.phoneValidator, Validators.pattern("[0-9+]*")]
                : [Validators.required, this.phoneValidator, Validators.pattern("[0-9+]*")]
            );
        } else {
          this.patientForm
            .get("phone")
            .setValidators(
              this.isMinor
                ? [this.phoneValidator, Validators.pattern("[0-9+]*")]
                : [Validators.required, this.phoneValidator, Validators.pattern("[0-9+]*")]
            );
        }
      });
    this.patientForm
      .get("gender")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.downgradeIdentityIfNeeded(MODIFICATION_TYPE.gender);
      });
  }

  public downgradeIdentityIfNeeded(modificationType: MODIFICATION_TYPE): void {
    if (this.identityStatus === IDENTITY_STATUS.temporary || this.identityStatus === IDENTITY_STATUS.validated) {
      this.addNewIdentity(modificationType);
      return;
    }
    this.identityStatus -= 1;
    this.identityFromINSI = false;
    this.patientForm.get("oid")?.setValue("", { emitEvent: false });
    this.patientForm.get("ins")?.setValue("", { emitEvent: false });
    this.addNewIdentity(modificationType);
  }

  private addNewIdentity(modificationType: MODIFICATION_TYPE): void {
    const n = this.newIdentities.length;
    if (n > 0) {
      this.newIdentities[n - 1].period.end = moment().format();
    }
    const identity: IIdentity = {
      status: this.identityStatus,
      verification: this.patientForm.get("identityVerification").value,
      method: this.patientForm.get("identityVerificationMethod").value,
      modificationType,
      period: {
        start: moment().format(),
        end: undefined,
      },
      responsible: [
        {
          use: "usual",
          system: SYSTEM_COMUNICARE,
          value: this.sessionService.account.caremateIdentifier,
          label: "",
        },
      ],
    };
    this.newIdentities.push(identity);
  }

  private async initData() {
    // Get the monitoring links available to the selected service:
    const selectedService = this.userService.allServices.find((s) => s.serviceRef === this.selectedDepartment.reference);
    const links = selectedService?.linkedMonitoringOrganizations?.filter(
      (l) =>
        !this.userService.isMonitoringUser ||
        this.sessionService.currentMonitoringOrg?.reference === this.sessionService.allsOption ||
        l.organizationRef.reference === this.sessionService.currentMonitoringOrg.reference
    );
    this.monitoringOrgsRefs = links?.map((l) => l.organizationRef);

    for (const link of links) {
      // if the user is monitoring it's possible he does not have access to all the
      // monitoring services the normal selected service has access to, so we need
      // to filter.
      // if the user is not monitoring, by default, he has access to all monitoring
      // services his normal service has access to
      let servRefs = link.servicesRef;
      if (this.userService.isMonitoringUser) {
        const availableMonitServices = this.healthcareService.availableMonitoringServices().map((s) => s.serviceRef);
        servRefs = servRefs.filter((s) => availableMonitServices.includes(s.reference));
      }
      this.monitServicesLinks.set(link.organizationRef.reference, servRefs);
    }

    this.availableLangs = await this.formsData.getLanguages();
    this.availableGender = this.formsData.GENDER;
    this.availableDoctors = this.allPractitioners
      .filter((p) => this.practitionerService.getRole(p) === QUALIFICATIONS.DOCTOR_CODE && p.userStatus !== USER_STATUS.INACTIVE)
      .sort((a, b) => {
        return a?.name?.given[0]?.toLowerCase() < b?.name?.given[0]?.toLowerCase()
          ? -1
          : a?.name?.given[0]?.toLowerCase() > b?.name?.given[0]?.toLowerCase()
          ? 1
          : 0;
      });
    this.availableNurses = this.allPractitioners
      .filter((p) => this.practitionerService.getRole(p) === QUALIFICATIONS.NURSE_CODE && p.userStatus !== USER_STATUS.INACTIVE)
      .sort((a, b) => {
        return a?.name?.given[0]?.toLowerCase() < b?.name?.given[0]?.toLowerCase()
          ? -1
          : a?.name?.given[0]?.toLowerCase() > b?.name?.given[0]?.toLowerCase()
          ? 1
          : 0;
      });
    this.availableSecretaries = this.allPractitioners
      .filter((p) => this.practitionerService.getRole(p) === QUALIFICATIONS.SECRETARY_CODE && p.userStatus !== USER_STATUS.INACTIVE)
      .sort((a, b) => {
        return a?.name?.given[0]?.toLowerCase() < b?.name?.given[0]?.toLowerCase()
          ? -1
          : a?.name?.given[0]?.toLowerCase() > b?.name?.given[0]?.toLowerCase()
          ? 1
          : 0;
      });
    this.availablePharmacist = this.allPractitioners
      .filter((p) => this.practitionerService.getRole(p) === QUALIFICATIONS.PHARMACIST_CODE && p.userStatus !== USER_STATUS.INACTIVE)
      .sort((a, b) => {
        return a?.name?.given[0]?.toLowerCase() < b?.name?.given[0]?.toLowerCase()
          ? -1
          : a?.name?.given[0]?.toLowerCase() > b?.name?.given[0]?.toLowerCase()
          ? 1
          : 0;
      });

    if (!this.isCreation) {
      this.addMissingAssignedPractitioners();
    }
    this.updateForm();
    if (!this.isCreation) {
      this.isLoading = false;
    }
  }

  private addMissingAssignedPractitioners() {
    const careProviders = this.patient?.patient?.careProvider;
    if (!careProviders) return;

    const checks: { ref: string | undefined; list: Practitioner[] }[] = [
      { ref: careProviders[0]?.reference, list: this.availableDoctors },
      { ref: careProviders[1]?.reference, list: this.availableNurses },
      { ref: careProviders[2]?.reference, list: this.availableSecretaries },
      { ref: careProviders[3]?.reference, list: this.availablePharmacist },
    ];

    for (const { ref, list } of checks) {
      if (!ref) continue;
      const alreadyPresent = list.some((p) => FHIRHelper.getMainIdentifier(p)?.value === ref);
      if (!alreadyPresent) {
        const missing = this.allPractitioners.find((p) => FHIRHelper.getMainIdentifier(p)?.value === ref);
        if (missing) {
          list.unshift(missing);
        }
      }
    }
  }

  private setupMonitOrgWatch() {
    if (this.patientForm.get("monitoringOrgsLink")) {
      this.monitLinkSub$?.unsubscribe();
      this.monitLinkSub$ = this.patientForm
        .get("monitoringOrgsLink")
        .valueChanges.pipe(takeUntil(this.onDestroy$))
        .subscribe((orgs: Reference[]) => {
          this.setupMonitoringServices(orgs);
          if (!orgs?.length) {
            this.careplanRequiredForMonitoringEmail = false;
          }
        });
    }
  }

  private setupMonitoringServices(orgs: Reference[], servs?: Reference[], monitPract?: Reference[]) {
    const hasOrgs = orgs?.length > 0;

    if (hasOrgs && !this.patientForm.contains("monitoringServicesLink")) {
      this.patientForm.addControl(
        "monitoringServicesLink",
        new UntypedFormControl([], [(c: AbstractControl) => (c.value?.length ? null : { required: true })])
      );
      if (!this.patientForm.contains("monitoringPractitioners")) {
        this.patientForm.addControl("monitoringPractitioners", new UntypedFormControl(null));
      }
    }

    if (hasOrgs) {
      this.monitoringServicesRefs = this.filterServicesFromSelectedOrgs(orgs);
      this.monitoringServiceLocked = this.userService.isMonitoringUser && this.userService.allMonitoringServices?.length === 1;
      const availableRefs = this.monitoringServicesRefs?.map((s) => s.reference) ?? [];
      if (servs?.length) {
        let selectedServices = servs.filter((s) => availableRefs.includes(s.reference));
        if (this.monitoringServiceLocked && !selectedServices.length) {
          selectedServices = [this.monitoringServicesRefs[0]];
        }
        this.patientForm.get("monitoringServicesLink").patchValue(selectedServices);
        this.setupMonitoringPractitioners(selectedServices, monitPract);
      } else {
        const currentServices: Reference[] = this.patientForm.get("monitoringServicesLink")?.value ?? [];
        let filteredServices = currentServices.filter((s) => availableRefs.includes(s.reference));
        if (this.monitoringServiceLocked && !filteredServices.length) {
          filteredServices = [this.monitoringServicesRefs[0]];
        }
        this.patientForm.get("monitoringServicesLink").patchValue(filteredServices);
        this.setupMonitoringPractitioners(filteredServices, monitPract);
      }
      if (this.monitoringServiceLocked) {
        this.patientForm.get("monitoringServicesLink").disable();
      } else {
        this.patientForm.get("monitoringServicesLink").enable();
      }
      this.monitServLinkSub$?.unsubscribe();
      this.monitServLinkSub$ = this.patientForm
        .get("monitoringServicesLink")
        .valueChanges.pipe(takeUntil(this.onDestroy$))
        .subscribe((services: Reference[]) => {
          this.setupMonitoringPractitioners(services);
        });
    } else {
      this.monitServLinkSub$?.unsubscribe();
      if (this.patientForm.contains("monitoringServicesLink")) {
        this.patientForm.removeControl("monitoringServicesLink");
      }
      if (this.patientForm.contains("monitoringPractitioners")) {
        this.monitoringPractitioners = [];
        this.patientForm.removeControl("monitoringPractitioners");
      }
    }
  }

  private setupMonitoringPractitioners(services: Reference[], monitPract?: Reference[]): void {
    if (!services?.length) {
      if (this.patientForm.contains("monitoringPractitioners")) {
        this.monitoringPractitioners = [];
        this.patientForm.removeControl("monitoringPractitioners");
      }
      this.monitoringPractitionersByOrg.clear();
      return;
    }

    if (!this.patientForm.contains("monitoringPractitioners")) {
      this.patientForm.addControl("monitoringPractitioners", new UntypedFormControl(null));
    }

    forkJoin(services.map((s) => this.practitionerService.listForService(s.reference).pipe(first())))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((results) => {
        const seen = new Set<string>();
        this.monitoringPractitioners = results.flat().filter((p) => {
          const ref = p.asReference?.reference;
          if (!ref) return true;
          if (seen.has(ref)) return false;
          seen.add(ref);
          return true;
        });

        this.monitoringPractitionersByOrg.clear();
        services.forEach((service, index) => {
          const orgRef = this.userService.allMonitoringServices.find((s) => s.serviceRef === service.reference)?.providedBy?.reference;
          if (orgRef) {
            const orgSet = this.monitoringPractitionersByOrg.get(orgRef) ?? new Set<string>();
            results[index].forEach((p) => {
              if (p.asReference?.reference) orgSet.add(p.asReference.reference);
            });
            this.monitoringPractitionersByOrg.set(orgRef, orgSet);
          }
        });
        const initialMonitPract = monitPract?.length ? monitPract : null;
        const previouslySelected: Reference[] = this.patientForm.get("monitoringPractitioners")?.value ?? initialMonitPract ?? [];
        if (this.monitoringPractitioners?.length && previouslySelected.length) {
          const allRefs = new Set(previouslySelected.map((p) => p.reference));
          const stillAvailable = this.monitoringPractitioners.filter((p) => allRefs.has(p.asReference.reference)).map((p) => p.asReference);
          this.patientForm.get("monitoringPractitioners").patchValue(stillAvailable.length ? stillAvailable : null);
        } else {
          this.patientForm.get("monitoringPractitioners").patchValue(null);
        }
      });
  }

  private filterServicesFromSelectedOrgs(orgs: Reference[]): Reference[] {
    const result: Reference[] = [];
    for (const org of orgs ?? []) {
      result.push(...(this.monitServicesLinks.get(org.reference) ?? []));
    }
    return result;
  }

  public async apply(closeAfterSave = false): Promise<void> {
    if (this.country === this.FRANCE && !this.patientForm.get("birthplace").value) {
      // makeBirthplaceRequired() triggers a value change detection triggering a downgradeIdentityIfNeeded(). To avoid this, we make "birthplace" required only if its value is not set.
      this.makeBirthplaceRequired();
    }
    if (
      this.patientForm.valid &&
      (this.townServerSideSearch?.townServerSideFilteringCtrl ? !this.townServerSideSearch?.townServerSideFilteringCtrl.invalid : true) // we use !invalid instead of valid so disabled field return true as well
    ) {
      if (this.isCreation) {
        this.addPatient(this.getPatientFromForm());
      } else {
        const formData = this.getPatientFromForm();
        const newPatient = this.transformAddPatientDataToPatient(formData, this.patient.patient);

        const oldMonitoring = this.patient?.patient?.monitoringOrganizations;
        const newMonitoring = newPatient?.monitoringOrganizations;
        let confirmed = true;

        if (oldMonitoring?.length && (!newMonitoring || newMonitoring.length === 0)) {
          const message = this.translateService.instant("forms.monitDeleteConfirm");
          confirmed = await this.dialog
            .open(ConfirmationDialogComponent, {
              data: { message, type: ConfirmationDialogType.CONFIRM },
            })
            .afterClosed()
            .toPromise();

          if (!confirmed) {
            this.statService.createStatEvent("Edit patient - Monitoring deletion denied", [this.selectedDepartment.reference]);
            return;
          } else {
            this.statService.createStatEvent("Edit patient - Monitoring deletion accepted", [this.selectedDepartment.reference]);
          }
        } else if (oldMonitoring?.length && newMonitoring?.length) {
          const oldServiceRefs = new Set(oldMonitoring.flatMap((o) => o.servicesRef?.map((s) => s.reference) ?? []));
          const newServiceRefs = new Set(newMonitoring.flatMap((o) => o.servicesRef?.map((s) => s.reference) ?? []));
          const servicesChanged =
            oldServiceRefs.size !== newServiceRefs.size || [...oldServiceRefs].some((ref) => !newServiceRefs.has(ref));
          if (servicesChanged) {
            const oldNames = oldMonitoring.flatMap((o) => o.servicesRef?.map((s) => s.display) ?? []).join(", ");
            const newNames = newMonitoring.flatMap((o) => o.servicesRef?.map((s) => s.display) ?? []).join(", ");
            const message = this.translateService.instant("forms.monitChangeConfirm", {
              MONIT_NAME: oldNames,
              NEW_MONIT_NAME: newNames,
            });
            confirmed = await this.dialog
              .open(ConfirmationDialogComponent, {
                data: { message, type: ConfirmationDialogType.CONFIRM },
              })
              .afterClosed()
              .toPromise();

            if (!confirmed) {
              this.statService.createStatEvent("Edit patient - Monitoring change denied", [this.selectedDepartment.reference]);
              return;
            } else {
              this.statService.createStatEvent("Edit patient - Monitoring change accepted", [this.selectedDepartment.reference]);
            }
          }
        }

        this.putPatient(newPatient, closeAfterSave);
      }
    } else {
      const invalid = [];
      const controls = this.patientForm.controls;
      for (const name in controls) {
        if (controls[name].invalid) {
          invalid.push(name);
        }
      }
      FileLogger.warn("AddPatientComponent", "invalid form");
    }
    this.submitted = true;
    this.patientForm.markAllAsTouched();
  }

  private getPatientFromForm(): IAddPatientData {
    const Qdata = {
      ...this.patientForm.getRawValue(),
      noSms: this.noSms,
      userTest: this.userTest,
    };
    const patient: IAddPatientData = {
      name: Qdata.name,
      firstname: Qdata.firstname,
      mail: Qdata.mail,
      phone: Qdata.phone,
      internalIdentifier: Qdata.internalId,
      insurance: Qdata.insurance ? Qdata.insurance.organizationReference : null,
      insuranceRef: Qdata.insurance ? Qdata.insurance.asReference : null,
      gender: Qdata.gender,
      careProvider: this.practitionerService.getPractitionerReference(Qdata.practitioners.doc),
      nurse: Qdata.practitioners.nurse ? this.practitionerService.getPractitionerReference(Qdata.practitioners.nurse) : null,
      secretary: Qdata.practitioners.secretary ? this.practitionerService.getPractitionerReference(Qdata.practitioners.secretary) : null,
      pharmacist: Qdata.practitioners.pharmacist ? this.practitionerService.getPractitionerReference(Qdata.practitioners.pharmacist) : null,
      language: Qdata.userLang,
      birthdate: moment(Qdata.birthDate).format("YYYY-MM-DD"),
      testUser: Qdata.userTest,
      noSms: Qdata.noSms,
      insuranceIdentifier: Qdata.insuranceId,
      SSIN: Qdata.SSIN,
      organization: Qdata.organization,
      healthcareService: Qdata.healthcareService,
      pseudoname: Qdata.pseudoname ? Qdata.pseudoname : null,
      // FR fields
      firstnames: Qdata.firstnames
        ?.split(",")
        .map((s) => s.trim())
        .filter(Boolean), // remove empty strings if the input finishes with a coma
      useName: Qdata.useName,
      useFirstname: Qdata.useFirstname,
      birthplace: Qdata.birthplace,
      //@TODO CMATE-4621 : add identity: IIdentity[]
      ins: Qdata.ins,
      oid: Qdata.oid,
      identity: this.newIdentities,
      historicINS: this.insHistory?.map((el) => ({
        ins: el.MatriculeIns,
        oid: el.Oid,
        begin: el.dateDeb,
        end: el.dateFin,
      })),
      ethnicGroup: Qdata.ethnicGroup,
      note: Qdata.note?.trim() || undefined,
    };

    if (Qdata.monitoringServicesLink?.length) {
      const selectedServiceRefs: Reference[] = Qdata.monitoringServicesLink;
      const orgMap = new Map<string, { orgRef: Reference; services: Reference[] }>();
      for (const serviceRef of selectedServiceRefs) {
        const currentMonitService = this.userService.allMonitoringServices.find((s) => s.serviceRef === serviceRef?.reference);
        if (currentMonitService) {
          const orgRefStr = currentMonitService.providedBy.reference;
          if (!orgMap.has(orgRefStr)) {
            orgMap.set(orgRefStr, { orgRef: currentMonitService.providedBy, services: [] });
          }
          orgMap.get(orgRefStr).services.push(serviceRef);
        }
      }
      if (orgMap.size > 0) {
        patient.monitoringOrganizations = Array.from(orgMap.values()).map(({ orgRef, services }) => {
          const orgPractRefs = this.monitoringPractitionersByOrg.get(orgRef.reference);
          const practitionersRef = (Qdata.monitoringPractitioners ?? []).filter((p: Reference) =>
            orgPractRefs ? orgPractRefs.has(p.reference) : true
          );
          // Preserve services within this org that the user cannot see/modify
          const availableServicesForOrg = this.monitServicesLinks.get(orgRef.reference) ?? [];
          const inaccessibleServices = !this.isCreation
            ? (
                this.patient?.patient.monitoringOrganizations?.find((mo) => mo.organizationRef.reference === orgRef.reference)
                  ?.servicesRef ?? []
              ).filter((s) => !availableServicesForOrg.some((avail) => avail.reference === s.reference))
            : [];
          return {
            organizationRef: orgRef,
            servicesRef: [...services, ...inaccessibleServices],
            practitionersRef: practitionersRef.length ? practitionersRef : undefined,
          };
        });
      } else if (!this.isCreation) {
        patient.monitoringOrganizations = this.patient?.patient.monitoringOrganizations;
      } else {
        patient.monitoringOrganizations = [];
      }
    } else if (this.patientForm.contains("monitoringOrgsLink") && !Qdata.monitoringServicesLink?.length) {
      patient.monitoringOrganizations = [];
    } else if (!this.isCreation) {
      patient.monitoringOrganizations = this.patient?.patient.monitoringOrganizations;
    } else {
      patient.monitoringOrganizations = [];
    }

    // France has only one (fictitious) mutual insurance company - The French state
    if (this.country === this.FRANCE && this.availableInsurances.length === 1 && patient.insuranceIdentifier) {
      patient.insuranceRef = this.availableInsurances[0].asReference;
      patient.insurance = this.availableInsurances[0].organizationReference;
    }

    let adr: IAddress;
    if (Qdata.addressLine1 && Qdata.addressCP && Qdata.addressCity && Qdata.addressCountry) {
      adr = {
        use: "home",
        line: [Qdata.addressLine1],
        city: Qdata.addressCity,
        postalCode: Qdata.addressCP,
        country: Qdata.addressCountry,
      };

      if (Qdata.addressLine2) {
        adr.line.push(Qdata.addressLine2);
      }
    }
    patient.address = Tools.isDefined(adr) ? [adr] : [];

    if ((!Tools.isDefined(patient.phone) || patient.phone === "") && patient.noSms) {
      patient.noSms = false;
      this.noSms = false;
    }
    patient.address = Tools.isDefined(adr) ? [adr] : [];
    return patient;
  }

  private transformAddPatientDataToPatient(Qdata: IAddPatientData, unmodifiedPatient: IPatient): IPatient {
    const newPatient = Tools.deepCopy(unmodifiedPatient);
    // France has only one (fictitious) mutual insurance company - The French state
    if (this.country === this.FRANCE && this.availableInsurances.length === 1 && Qdata.insuranceIdentifier) {
      Qdata.insuranceRef = this.availableInsurances[0].asReference;
      Qdata.insurance = this.availableInsurances[0].organizationReference;
    }

    if (newPatient.name.length) {
      newPatient.name[0].family = [Qdata.name];
      newPatient.name[0].given = [Qdata.firstname, ...(Qdata.firstnames ? Qdata.firstnames : [])];
      newPatient.name[0].prefix = [this.patientService.getPrefixFromGenderAndLang(Qdata)];
      newPatient.name[0].use = "official";
      newPatient.name[0].text = `${Qdata.name} ${Qdata.firstname}`;
    }
    newPatient.birthDate = moment(Qdata.birthdate).format("YYYY-MM-DD");
    newPatient.gender = Qdata.gender;
    newPatient.ethnicGroup = Qdata.ethnicGroup;
    this.patientService.setPreferredLang(newPatient, Qdata.language);
    this.patientService.setMail(newPatient, Qdata.mail);
    this.patientService.setPhone(newPatient, Qdata.phone);

    this.patientService.setInsu(newPatient, Qdata.insuranceIdentifier, Qdata.insuranceRef);
    this.patientService.setInternalId(newPatient, Qdata.internalIdentifier);
    this.patientService.setCareProviders(newPatient, Qdata.careProvider, Qdata.nurse, Qdata.secretary, Qdata.pharmacist);
    this.patientService.setSSIN(newPatient, Qdata.SSIN);
    newPatient.pseudoname = Qdata.pseudoname ? Qdata.pseudoname : null;
    /*
      The this.monitoringOrgsRefs variable indicates the monitoring services associated with the selected service.
      If there are none, the patient's monitoring services should not be changed (as the doctor was not aware that monitoring was taking place).
      */
    if (!this.monitoringOrgsRefs?.length) {
      newPatient.monitoringOrganizations = newPatient.monitoringOrganizations ?? [];
    } else {
      // Preserve monitoring entries for orgs outside the current user's visible scope
      // (example: a monitoring user can only see their own org; other orgs must not be erased)
      const accessibleOrgRefs = new Set(this.monitoringOrgsRefs.map((o) => o.reference));
      const untouchedOrgs = (newPatient.monitoringOrganizations ?? []).filter((mo) => !accessibleOrgRefs.has(mo.organizationRef.reference));
      newPatient.monitoringOrganizations = [...untouchedOrgs, ...(Qdata.monitoringOrganizations ?? [])];
    }
    newPatient.address = Qdata.address ?? [];
    newPatient.note = Qdata.note?.trim() || undefined;

    // Specific for FR patients :
    if (this.country === this.FRANCE) {
      // add useName and useFirstname
      if (Qdata.useName || Qdata.useFirstname) {
        newPatient.name = newPatient.name.filter((name) => name.use !== "usual");
        newPatient.name.push({
          family: [Qdata.useName],
          given: [Qdata.useFirstname],
          prefix: undefined,
          use: "usual",
          text: `${Qdata.useName} ${Qdata.useFirstname}`,
        });
      } else {
        newPatient.name = newPatient.name.filter((name) => name.use !== "usual");
      }
      newPatient.birthplace = Qdata.birthplace;

      // Save INS in the identifier if it has changed since the first registration
      const ins = PatientHelper.getIns(unmodifiedPatient);
      const oid = PatientHelper.getOid(unmodifiedPatient);
      if ((ins && !Qdata.ins) || (oid && !Qdata.oid) || (Qdata.ins && Qdata.ins !== ins) || (Qdata.oid && Qdata.oid !== oid)) {
        this.patientService.setINS(newPatient, Qdata.ins, Qdata.oid);
      }
      // Save INS history in IIdentifier (only new elements are added)
      if (Tools.isDefined(this.insHistory) && this.insHistory.length > 0) {
        const historicINSIdentifier: IIdentifier[] = [];
        for (const h of this.insHistory) {
          const existingINS = newPatient.identifier.find((identifier) => identifier.value === h.MatriculeIns);
          if (!existingINS) {
            const insIdentifier: IIdentifier = {
              use: FHIRHelper.USE_MATRICULE_INS,
              system: FHIRHelper.SYSTEM_MATRICULE_INS,
              value: h.MatriculeIns,
              period: {
                start: h.dateDeb ? h.dateDeb : moment().format(),
                end: h.dateFin ? h.dateFin : moment().format(),
              },
              type: {
                code: h.Oid,
                display: MatriculeInsHelper.computeDisplayOID(h.Oid),
                system: FHIRHelper.SYSTEM_TYPE_MATRICULE_INS,
              },
            };
            historicINSIdentifier.push(insIdentifier);
          }
        }

        // Add the historicINSIdentifier to newPatient's identifier
        newPatient.identifier.push(...historicINSIdentifier);
      }

      newPatient.identity = Qdata.identity;
    }
    return newPatient;
  }

  private addPatient(data: IAddPatientData, forceUpdateUser?: string, needChangeInsurance = false, needChangeAddress = false) {
    this.saveInProgress = true;
    this.isLoading = true;
    const sendMailToMonitoring = true;

    this.patientService
      .create(
        data,
        forceUpdateUser
          ? {
              forceUpdateUser,
              needChangeInsurance,
              sendMailToMonitoring,
              needChangeAddress,
              careplanRequiredForMonitoringEmail: this.careplanRequiredForMonitoringEmail && sendMailToMonitoring,
              careplanRequiredForSignup: this.careplanRequiredForSignup && !this.noSms,
            }
          : {
              sendMailToMonitoring,
              careplanRequiredForMonitoringEmail: this.careplanRequiredForMonitoringEmail && sendMailToMonitoring,
              careplanRequiredForSignup: this.careplanRequiredForSignup && !this.noSms,
            }
      )
      .subscribe(
        (patientSaved: IPatient) => {
          this.dialog.closeAll(); // close modals before showing the success message
          this.success(patientSaved, forceUpdateUser);
        },
        (err) => {
          if (err?.message === ERROR_MSG.ALREADY_EXISTS && err?.additionalData) {
            this.statService.createStatEvent("Tried to create a patient that already exists", [this.selectedDepartment.reference]);
            // open new modal and inject the form's patient object and the already existing patient passed with the error
            const dialogRef = this.dialog.open(PatientAlreadyExistsDialogComponent, {
              disableClose: true,
              data: { ...err, ...data },
              panelClass: "dialog-container-scroll",
            });

            dialogRef.afterClosed().subscribe((result) => {
              if (result?.forceUpdateUser) {
                // don't send sms on update (ignore checkbox by passing to true)
                data.noSms = true;
                this.addPatient(data, result.forceUpdateUser, result.needChangeInsurance, result.needChangeAddress);
              }
            });
          } else {
            this.fail(err, data);
          }
          this.saveInProgress = false;
          this.isLoading = false;
        },
        () => {
          this.sessionService.needRefreshPatientDataList();
          this.sessionService.needRefreshPatientUser(); // make sure PU is refreshed after edition, important when you are on the patient page of the patient being edited to update the form correctly.
          this.saveInProgress = false;
          this.isLoading = false;
        }
      );
  }

  private putPatient(data: IPatient, closeAfterSave = false) {
    this.saveInProgress = true;
    this.isLoading = true;

    this.patientService.update(data, this.patient?.user.anonymous).subscribe(
      () => {
        this.statService.createStatEvent("Edit patient", [this.selectedDepartment.reference]);
        if (closeAfterSave) {
          this.patientRegistrationService.back();
        }
      },
      (err) => {
        // when error, update variables and re-evaluate the related controls to show mat-error
        this.emailAlreadyTaken = err.additionalData?.emailAlreadyTaken;
        this.patientForm.controls.mail.updateValueAndValidity();
        this.patientForm.controls.phone.updateValueAndValidity();
        this.fail(err);
        this.saveInProgress = false;
        this.isLoading = false;
      },
      () => {
        if (!closeAfterSave) {
          // if we do not close, we must update the patient variable with the new changes
          this.patientService
            .getPatientUser(this.patient.user.caremateIdentifier)
            .pipe(
              takeUntil(this.onDestroy$),
              finalize(() => {
                this.sessionService.needRefreshPatientDataList();
                this.saveInProgress = false;
                this.isLoading = false;
              })
            )
            .subscribe(
              (patientUser) => {
                this.patient = patientUser;
              },
              (err) => {
                FileLogger.error("PatientFormComponent", "putPatient - getPatientUser error", err, "none");
              }
            );
        } else {
          this.sessionService.needRefreshPatientDataList();
          this.saveInProgress = false;
          this.isLoading = false;
        }
      }
    );
  }

  private success(patientSaved: IPatient, forceUpdateUser?: string): void {
    this.patientRegistrationService.onPatientCreated$.next({ patientSaved, forceUpdateUser });
    if (this.patientRegistrationService.isLastStep) {
      this.patientRegistrationService.onFinish();
    } else {
      this.patientRegistrationService.onNextStep$.next();
    }
  }

  private fail(err: IAppError, patientFallback?: IAddPatientData): void {
    this.translateService.get("common.saveFail").subscribe((trans) => {
      if (err?.message === ERROR_MSG.ALREADY_EXISTS) {
        this.statService.createStatEvent("Tried to save a patient that already exists", [this.selectedDepartment.reference]);
        trans += ` : ${this.translateService.instant("api.errors.alreadyExist")}`;
      } else if (err?.message === ERROR_MSG.INVALID_DATA) {
        this.statService.createStatEvent("Save patient failed: invalid data", [this.selectedDepartment.reference]);
        trans += ` : ${this.translateService.instant("api.errors.invalidData")}`;
      } else if (err?.message === ERROR_MSG.INVALID_EMAIL) {
        this.statService.createStatEvent("Save patient failed: invalid email", [this.selectedDepartment.reference]);
        trans += ` : ${this.translateService.instant("api.errors.invalidEmail")}`;
        this.patientForm.get("mail").reset();
      }
      this.snackBar
        .open(trans, this.translateService.instant("common.retry"), {
          duration: 3000,
        })
        .onAction()
        .subscribe(async () => {
          if (this.isCreation) {
            this.addPatient(patientFallback);
          } else {
            const fallback = patientFallback ? patientFallback : this.getPatientFromForm();
            const newPatientFallback = this.transformAddPatientDataToPatient(fallback, this.patient?.patient);
            this.putPatient(newPatientFallback);
          }
        });
    });
  }

  public isInternalIdValid(): boolean {
    return this.patientForm.controls[CONTROL_KEYS.internalId].value && this.patientForm.controls[CONTROL_KEYS.internalId].valid;
  }

  private updateForm() {
    let selectedOrgs: Reference[] = [];
    const selectedServices: Reference[] = [];
    const selectedMonitPractitioners: Reference[] = [];
    if (!this.isCreation) {
      // Find the monit link already selected for the patient:
      const monitLinksRefs: string[] = this.patient?.patient.monitoringOrganizations?.map((link) => link.organizationRef.reference);
      selectedOrgs = this.monitoringOrgsRefs?.filter((o) => monitLinksRefs?.includes(o.reference));
      if (selectedOrgs?.length > 0) {
        for (const org of selectedOrgs) {
          const monitOrgLink = this.patient?.patient.monitoringOrganizations.find((o) => o.organizationRef.reference === org.reference);
          if (monitOrgLink) {
            const availableMonitServices = this.monitServicesLinks.get(org.reference)?.map((s) => s.reference) ?? [];
            selectedServices.push(...(monitOrgLink.servicesRef?.filter((s) => availableMonitServices.includes(s.reference)) ?? []));
            selectedMonitPractitioners.push(...(monitOrgLink.practitionersRef ?? []));
          }
        }
      }
      const insurance = this.patientService.getInsurance(this.patient?.patient, this.availableInsurances);
      const address = this.patient?.patient.address?.find((a) => a.use === "home");

      // update form when modifying existing patient
      this.patientForm.patchValue(
        {
          name: this.patientService.getSurname(this.patient?.patient),
          firstname: this.patientService.getFirstname(this.patient?.patient),
          pseudoname: this.patient?.patient.pseudoname,
          birthDate: this.patient?.patient.birthDate,
          phone: this.patientService.getPhone(this.patient?.patient),
          mail: this.patientService.getMail(this.patient?.patient),
          userLang: this.formsData.getPreferredLang(this.patient?.patient),
          gender: this.patient?.patient.gender,
          practitioners: {
            secretary: this.patientService.getSecretary(this.patient?.patient, this.availableSecretaries),
            pharmacist: this.patientService.getPharmacist(this.patient?.patient, this.availablePharmacist),
            nurse: this.patientService.getNurse(this.patient?.patient, this.availableNurses),
            doc: this.patientService.getDoctor(this.patient?.patient, this.availableDoctors),
          },
          internalId: this.patientService.getInternalId(this.patient?.patient),
          insurance: insurance,
          insuranceId: this.patientService.getInsurancelId(this.patient?.patient),
          SSIN: this.patientService.getSSIN(this.patient?.patient),
          ethnicGroup: this.patient?.user.ethnicGroup,
          addressLine1: address?.line?.[0],
          addressLine2: address?.line?.[1],
          addressCP: address?.postalCode,
          addressCity: address?.city,
          addressCountry: address?.country,
          note: this.patient?.patient?.note,
        },
        { emitEvent: false, onlySelf: true }
      );

      if (insurance || this.country === this.FRANCE) {
        this.patientForm.get("insuranceId").enable();
      } else {
        this.patientForm.get("insuranceId").setValue(null);
        this.patientForm.get("insuranceId").disable();
      }

      this.patientForm.get("mail").disable();

      if (this.patientService.isMinor(this.patient?.patient)) {
        // minor without phone number
        this.patientForm.get("phone").setValidators([this.phoneValidator, Validators.pattern("[0-9+]*")]);
        this.phoneNotRequired = true;
      } else {
        this.patientForm.get("phone").setValidators([Validators.required, this.phoneValidator, Validators.pattern("[0-9+]*")]);
        this.phoneNotRequired = false;
      }

      if (this.country === this.FRANCE) {
        const firstnames =
          this.patient?.patient.name[0].given.length > 1
            ? this.patient?.patient.name[0].given.slice(1).length > 0
              ? this.patient?.patient.name[0].given.slice(1).join(", ")
              : this.patient?.patient.name[0].given.toString()
            : this.patient?.patient.name[0].given.toString();
        this.patientForm.get("firstnames").patchValue(firstnames.toUpperCase(), { emitEvent: false, onlySelf: true });

        const usualNameFirstname = this.patient?.patient?.name?.find((name) => name.use === "usual");
        const usualName =
          Array.isArray(usualNameFirstname?.family) && usualNameFirstname?.family?.length > 0 ? usualNameFirstname?.family[0] : undefined;
        const usualFirstname =
          Array.isArray(usualNameFirstname?.given) && usualNameFirstname?.given?.length > 0 ? usualNameFirstname?.given[0] : undefined;
        if (usualName) {
          this.patientForm.get("useName").patchValue(usualName, { emitEvent: false, onlySelf: true });
        }
        if (usualFirstname) {
          this.patientForm.get("useFirstname").patchValue(usualFirstname, { emitEvent: false, onlySelf: true });
        }

        this.patientForm
          .get("ins")
          .patchValue(this.patient?.patient.identifier.find((i) => i.use === FHIRHelper.USE_MATRICULE_INS && !i.period.end)?.value, {
            emitEvent: false,
            onlySelf: true,
          });
        this.patientForm
          .get("oid")
          .patchValue(this.patient?.patient.identifier.find((i) => i.use === FHIRHelper.USE_MATRICULE_INS && !i.period.end)?.type?.code, {
            emitEvent: false,
            onlySelf: true,
          });
        const latestIdentityIdx = this.patient?.patient.identity?.length;
        if (latestIdentityIdx !== undefined && latestIdentityIdx > 0) {
          const latestIdentity = this.patient?.patient.identity[latestIdentityIdx - 1];
          this.identityStatus = +latestIdentity.status as IDENTITY_STATUS;
          this.identityFromINSI = this.identityStatus === IDENTITY_STATUS.recovered || this.identityStatus === IDENTITY_STATUS.qualified;
          this.patientForm
            .get("identityVerification")
            .patchValue(Tools.isDefined(latestIdentity.verification) ? +latestIdentity.verification : null, {
              emitEvent: false,
              onlySelf: true,
            });
          this.patientForm
            .get("identityVerificationMethod")
            .patchValue(Tools.isDefined(latestIdentity.method) ? +latestIdentity.method : null, { emitEvent: false, onlySelf: true });
          this.newIdentities = this.patient?.patient.identity;
        }
        this.disableINSRetrievedField();
      }
    } else {
      // creation: pre-select from session if a specific monitoring org/service is already chosen
      const currentMonitOrg = this.sessionService.currentMonitoringOrg;
      const currentMonitService = this.sessionService.currentMonitoringService;
      if (
        currentMonitOrg?.reference !== this.sessionService.allsOption &&
        this.monitoringOrgsRefs?.some((o) => o.reference === currentMonitOrg?.reference)
      ) {
        selectedOrgs = [currentMonitOrg];
      }
      if (currentMonitService?.reference && currentMonitService.reference !== this.sessionService.allsOption) {
        selectedServices.push(currentMonitService);
      }
    }
    if (this.patientForm.contains("monitoringOrgsLink")) {
      this.monitLinkSub$?.unsubscribe();
      this.monitoringOrgLocked = this.userService.isMonitoringUser && this.userService.allMonitoringOrganizations?.length === 1;
      const orgsToSelect = this.monitoringOrgLocked && !selectedOrgs?.length ? [this.monitoringOrgsRefs[0]] : selectedOrgs;
      this.patientForm.get("monitoringOrgsLink").patchValue(orgsToSelect);
      if (this.monitoringOrgLocked) {
        this.patientForm.get("monitoringOrgsLink").disable();
      } else {
        this.patientForm.get("monitoringOrgsLink").enable();
      }
      this.setupMonitoringServices(orgsToSelect, selectedServices, selectedMonitPractitioners);
      this.setupMonitOrgWatch();
    } else {
      this.setupMonitoringServices(selectedOrgs, selectedServices, selectedMonitPractitioners);
    }
  }

  public computeFormData(): void {
    this.$allPractitioners = this.practitionerService.listForService(this.selectedDepartment?.reference);
    this.$allPractitioners
      .pipe(
        first(),
        takeUntil(this.onDestroy$),
        concatMap((p) => {
          this.allPractitioners = p;
          return this.$insuOrganizations;
        }),
        concatMap((i) => {
          this.availableInsurances = i;
          this.filteredInsurances = i;
          return this.$preferences;
        }),
        concatMap((prefs) => {
          this.goToCp = this.isAnonymous ? true : prefs ? prefs.goToCp : true;
          this.patientRegistrationService.onNeedCareplanChanged$.next(this.goToCp);
          return of(true);
        })
      )
      .subscribe((success) => {
        if (success) {
          this.initData();
        }
      });
  }

  public updateDepartment(event: MatSelectChange): void {
    this.selectedDepartment = event.value;
    this.setAvailableInsurancesFromSelectedDepartment();
    this.practitionerService
      .listForService(this.selectedDepartment.reference)
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe((p) => {
        this.allPractitioners = p;
        this.initData();
      });
  }

  public updateOrg(event: MatSelectChange): void {
    this.selectedOrg = event.value;
    const services = this.healthcareService.availableServices().filter((s) => s.providedBy.reference === this.selectedOrg.reference);
    this.departments = services.map((s) => s.asReference);
    this.selectedDepartment = this.departments[0];
    if (this.selectedDepartment) {
      this.patientForm.controls.healthcareService.setValue(this.selectedDepartment);
      this.setAvailableInsurancesFromSelectedDepartment();
      this.practitionerService
        .listForService(this.selectedDepartment.reference)
        .pipe(first(), takeUntil(this.onDestroy$))
        .subscribe((p) => {
          this.allPractitioners = p;
          this.initData();
        });
    }
  }

  public compareReference(o1: Reference, o2: Reference): boolean {
    return o1?.reference === o2?.reference;
  }

  public atLeastOneValidator: ValidatorFn = (control: UntypedFormGroup): ValidationErrors | null => {
    const controls = control.controls;
    if (controls) {
      const theOne = Object.keys(controls).findIndex((key) => controls[key].value !== null && controls[key].value !== undefined);
      if (theOne === -1) {
        return { atLeastOneRequired: true };
      }
    }
  };

  public phoneValidator: ValidatorFn = (control: AbstractControl): { [key: string]: true } | null => {
    return PhoneValidator.phoneValidator(control);
  };

  public emailValidator: ValidatorFn = (): ValidationErrors | null => {
    return this.emailAlreadyTaken ? { emailAlreadyTaken: true } : null;
  };

  public resetEmailAlreadyTaken(): void {
    this.emailAlreadyTaken = false;
  }

  public sendMail(): void {
    const mailText = "mailto:contact@comunicare.be?subject=Practitioner whish to use an existing email or phone";
    // TODO : choose correct mail subject
    window.location.href = mailText;
  }

  public insuranceDisplay(insurance: Organization): string {
    return insurance ? insurance.organizationName : "";
  }

  /**
   * Compute the value of availableInsurances based on the country of the selected department (when All is selected)
   */
  public setAvailableInsurancesFromSelectedDepartment(): void {
    const selectedDepartmentCountry = this.healthcareService
      .availableServices()
      .find((service) => service.mainId === this.selectedDepartment.reference)?.location.country;
    // in edition mode, the insurance field is never reset
    const countryChanged = this.isCreation && selectedDepartmentCountry !== this.selectedDepartmentCountry;
    this.selectedDepartmentCountry = selectedDepartmentCountry;

    // empty input from search when changing department
    if (countryChanged) {
      if (this.insInput?.nativeElement?.value) {
        this.insInput.nativeElement.value = "";
      }
      // reset the control too, otherwise setupForm() restores the previous insurance (top menu service change)
      this.patientForm?.get("insurance")?.setValue(undefined);
      this.patientForm?.get("insuranceId")?.setValue(undefined);
    }
    this.filteredInsurances = undefined;
    this.$insuOrganizations = this.organisationService.listSome("ins", selectedDepartmentCountry);
    this.$insuOrganizations.pipe(first()).subscribe((ins) => {
      this.availableInsurances = ins;
      this.filteredInsurances = ins;
    });
  }

  public insuranceAutoCompleteClose(): void {
    // Empty field when no insurance is selected from the list
    if (Tools.isString(this.patientForm.get("insurance").value)) {
      this.patientForm.get("insurance").setValue(undefined);
    }
  }

  private insPatientIsValid(response: InsResponse): boolean {
    return (
      response?.CodeCR === INSIReturnCode.ONE_INDENTITY_FOUND &&
      response?.DateNaissance?.length > 0 &&
      moment(response?.DateNaissance).isValid() &&
      response?.Prenom?.length > 0 &&
      response?.LieuNaissance?.length > 0 &&
      response?.ListePrenom?.length > 0 &&
      response?.NomNaissance?.length > 0 &&
      response?.Sexe?.length > 0
    );
  }

  public async searchForInsiPatient($event: boolean): Promise<void> {
    this.searchFinished = false;
    this.isSearching4InsiPatient = $event;
    this.searchIsLoading = $event;
    if (this.isSearching4InsiPatient) {
      const name = this.patientForm.get("name").value;
      let firstnames: string[] = this.patientForm
        .get("firstnames")
        .value?.split(",")
        ?.map((s) => s.trim())
        ?.filter(Boolean);

      if (!firstnames) {
        firstnames = [];
      }

      if (this.patientForm.get("firstname").value) {
        firstnames.unshift(this.patientForm.get("firstname").value);
      }
      firstnames = firstnames.filter(ArrayHelper.onlyUnique);

      const gender = this.patientForm.get("gender").value === "male" ? "M" : "F";
      const birthDate = moment(this.patientForm.get("birthDate").value).format("YYYY-MM-DD");
      // test each name separately
      for (const firstname of firstnames) {
        this.foundInsPatient = await this.insService.getINSIPatient(
          name,
          firstname,
          gender,
          birthDate,
          null,
          Boolean(this.patientForm.get("simulateIns").value)
        );
        if (this.insPatientIsValid(this.foundInsPatient)) {
          this.searchIsLoading = false;
          this.searchFinished = true;
          return;
        }
      }
      // if nothing is found, we test with all the first names at once
      if (firstnames.length > 1) {
        this.foundInsPatient = await this.insService.getINSIPatient(
          name,
          firstnames.reduce((f1, f2) => f1 + " " + f2),
          gender,
          birthDate,
          null,
          Boolean(this.patientForm.get("simulateIns").value)
        );
        if (this.insPatientIsValid(this.foundInsPatient)) {
          this.searchIsLoading = false;
          this.searchFinished = true;
          return;
        }
      }

      const birthplace: string = this.patientForm.get("birthplace").value?.reference
        ? this.patientForm.get("birthplace").value.reference
        : undefined;

      if (birthplace) {
        // if nothing is found, try each first name separately, adding the place of birth (if available).
        for (const firstname of firstnames) {
          this.foundInsPatient = await this.insService.getINSIPatient(
            name,
            firstname,
            gender,
            birthDate,
            birthplace,
            Boolean(this.patientForm.get("simulateIns").value)
          );
          if (this.insPatientIsValid(this.foundInsPatient)) {
            this.searchIsLoading = false;
            this.searchFinished = true;
            return;
          }
        }
        // if still nothing found, try again with all first names and place of birth (if available).
        if (firstnames.length > 1) {
          this.foundInsPatient = await this.insService.getINSIPatient(
            name,
            firstnames.reduce((f1, f2) => f1 + " " + f2),
            gender,
            birthDate,
            birthplace,
            Boolean(this.patientForm.get("simulateIns").value)
          );
          if (this.insPatientIsValid(this.foundInsPatient)) {
            this.searchIsLoading = false;
            this.searchFinished = true;
            return;
          }
        }
      }
      if (this.foundInsPatient?.CodeCR === INSIReturnCode.ONE_INDENTITY_FOUND && !this.insPatientIsValid(this.foundInsPatient)) {
        this.notUsableInsPatient = true;
      } else {
        this.notUsableInsPatient = false;
      }
    }
    this.searchFinished = true;
    this.searchIsLoading = false;
  }

  public transformToUpperCase(value: string, control: string): void {
    if (this.country === this.FRANCE) {
      value = value.trim();
      this.patientForm.get(control).patchValue(value.toUpperCase(), { emitEvent: false, onlySelf: true });
      if (control === "firstnames" && value.includes(",") && !this.patientForm.get("firstname").value) {
        const firstname = value
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)[0];
        if (firstname) {
          this.patientForm.get("firstname").patchValue(firstname.toUpperCase(), { emitEvent: true, onlySelf: true });
        }
      } else if (control === "firstnames" && !value.includes(",") && !this.patientForm.get("firstname").value) {
        this.patientForm.get("firstname").patchValue(value.toUpperCase(), { emitEvent: true, onlySelf: true });
      }
    }
  }

  public async useFoundInsPatient(): Promise<void> {
    this.searchIsLoading = true;
    this.patientForm.get("name").patchValue(this.foundInsPatient?.NomNaissance?.toUpperCase(), { emitEvent: false, onlySelf: true });
    this.patientForm
      .get("firstnames")
      .patchValue(this.foundInsPatient?.ListePrenom?.join(", ").toUpperCase(), { emitEvent: false, onlySelf: true });
    this.patientForm.get("gender").patchValue(this.foundInsPatient?.Sexe === "M" ? "male" : "female", { emitEvent: false, onlySelf: true });
    this.patientForm.get("birthDate").patchValue(this.foundInsPatient?.DateNaissance, { emitEvent: false, onlySelf: true });
    this.patientForm
      .get("birthplace")
      .patchValue(await this.getTownByCode(this.foundInsPatient?.DateNaissance, this.foundInsPatient?.LieuNaissance), {
        emitEvent: false,
        onlySelf: true,
      });
    this.refreshTownInput();
    this.patientForm.get("ins").patchValue(this.foundInsPatient?.MatriculeIns, { emitEvent: false, onlySelf: true });
    this.patientForm.get("oid").patchValue(this.foundInsPatient?.Oid, { emitEvent: false, onlySelf: true });
    this.insHistory = this.foundInsPatient.historiqueIns;
    this.showMore = true;
    this.identityFromINSI = true;
    this.computeIdentityStatus(MODIFICATION_TYPE.insiReturn);
    this.disableINSRetrievedField();
    this.foundInsPatient = undefined;
    this.searchIsLoading = false;
    this.isSearching4InsiPatient = false;
    const firstnamesFirst = this.patientForm
      .get("firstnames")
      .value?.split(",")
      .map((s) => s.trim())
      .filter(Boolean)[0];
    const writtenFirstname = this.patientForm.get("firstname").value?.split(" ")[0];
    if (firstnamesFirst !== writtenFirstname) {
      this.patientForm.get("firstname").invalid;
      this.patientForm.get("firstname").setErrors({
        isNotACorrectINSFirstnameError: true,
      });
    }
    // Mark fields modified by INS as touched to show error when necessary
    this.patientForm.get("ins").markAsTouched();
    this.patientForm.get("oid").markAsTouched();
    this.patientForm.get("firstname").updateValueAndValidity(); // firstname value must be re-evaluated to check validity
  }

  public cancelInsSearch(): void {
    this.isSearching4InsiPatient = false;
    this.foundInsPatient = undefined;
  }

  public onBirthdateChange(): void {
    // reset birthplace
    this.patientForm.get("birthplace").setValue(undefined, { emitEvent: false, onlySelf: true });
    this.refreshTownInput();
  }

  private async getTownByCode(date: string, code: string): Promise<IReference> {
    let town = await this.townsService.getTownByCode(date, code);
    if (!Tools.isDefined(town)) {
      town = {
        reference: code,
        display: "",
      };
    }
    return town;
  }

  private refreshTownInput() {
    // we need to set birthDate to undefined to trigger the ngIf of the birthplace input
    const value = this.patientForm.get("birthDate").value;
    this.patientForm.get("birthDate").setValue(undefined, { emitEvent: false, onlySelf: true });
    this.cdr.detectChanges();
    this.patientForm.get("birthDate").setValue(value, { emitEvent: false, onlySelf: true });
  }

  private checkUsersPermissions(): void {
    this.canUpdateRetrievedIdentityPermission = this.userService.isAuthorizedSync(null, "patient/retrievedIdentity", "PUT");
    this.canUseINSiService = this.userService.isAuthorizedSync(null, "dashboard/ins", "GET");
  }

  /**
   * Disable the INS fields of the FR patient if the user does not
   * have the right to update them
   */
  private disableINSRetrievedField(): void {
    if (!this.canUpdateRetrievedIdentityPermission && this.identityFromINSI) {
      this.patientForm.get("name").disable({ emitEvent: false, onlySelf: true });
      this.patientForm.get("firstnames").disable({ emitEvent: false, onlySelf: true });
      this.patientForm.get("gender").disable({ emitEvent: false, onlySelf: true });
      this.patientForm.get("birthDate").disable({ emitEvent: false, onlySelf: true });
      this.patientForm.get("ins").disable({ emitEvent: false, onlySelf: true });
      this.patientForm.get("oid").disable({ emitEvent: false, onlySelf: true });
    }
  }

  private enableFields(): void {
    this.patientForm.get("name").enable({ emitEvent: false, onlySelf: true });
    this.patientForm.get("firstnames").enable({ emitEvent: false, onlySelf: true });
    this.patientForm.get("gender").enable({ emitEvent: false, onlySelf: true });
    this.patientForm.get("birthDate").enable({ emitEvent: false, onlySelf: true });
    this.patientForm.get("ins").enable({ emitEvent: false, onlySelf: true });
    this.patientForm.get("oid").enable({ emitEvent: false, onlySelf: true });
  }

  public insValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const formatError = MatriculeInsHelper.insValidator(control);
    if (formatError) {
      return formatError;
    }
    if (!control.value?.length) {
      // An OID always designates the source of an INS, so the INS becomes required as soon as an OID is entered.
      return control.parent?.get("oid")?.value?.length ? { required: true } : null;
    }
    return null;
  };

  public oidValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value?.length) {
      // An INS must always be associated with an OID (the OID indicates the source of the INS),
      // so the OID becomes required as soon as an INS is entered.
      return control.parent?.get("ins")?.value?.length ? { required: true } : null;
    }
    const oid = MatriculeInsHelper.computeDisplayOID(control.value);
    return oid === "" ? { invalidOID: true } : null;
  };

  public firstnameValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (control.value?.length && control.value.length > 0 && this.country === this.FRANCE && this.identityFromINSI) {
      const firstnames = MatriculeInsHelper.namesStringToArray(this.patientForm.get("firstnames").value);
      const firstFirstname = MatriculeInsHelper.namesStringToArray(control.value);

      for (let index = 0; index < firstFirstname.length; index++) {
        if (firstFirstname[index] !== firstnames[index]) {
          return {
            isNotACorrectINSFirstnameError: true,
          };
        }
      }
    }
    return null;
  };

  public NameValidator: ValidatorFn = (control: AbstractControl): { [key: string]: true } | null => {
    const name: string = control.value;

    const firstLetterResult = this.checkFirstLetter(name);
    if (firstLetterResult) return firstLetterResult;

    const consecutiveApostrophesResult = this.checkConsecutiveApostrophes(name);
    if (consecutiveApostrophesResult) return consecutiveApostrophesResult;

    const consecutiveSpacesResult = this.checkConsecutiveSpaces(name);
    if (consecutiveSpacesResult) return consecutiveSpacesResult;

    // Check no more than 2 dashes
    if (name?.search("---") >= 0) {
      return { moreThanTwoDashes: true };
    }

    // Check for combinaison of space and apostrophe
    if (name?.search("' ") >= 0 || name?.search(" '") >= 0) {
      return { apostropheAndSpaceCombined: true };
    }
    return null;
  };

  public FirstNamesValidator: ValidatorFn = (control: AbstractControl): { [key: string]: true } | null => {
    if (control.value?.length && this.country === this.FRANCE) {
      const firstnames: string[] = control.value
        ?.split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      for (const firstname of firstnames) {
        const firstLetterResult = this.checkFirstLetter(firstname);
        if (firstLetterResult) return firstLetterResult;

        const consecutiveApostrophesResult = this.checkConsecutiveApostrophes(firstname);
        if (consecutiveApostrophesResult) return consecutiveApostrophesResult;

        const consecutiveSpacesResult = this.checkConsecutiveSpaces(firstname);
        if (consecutiveSpacesResult) return consecutiveSpacesResult;

        // The last character must be different from the hyphen or apostrophe
        const lastChar: string = firstname?.charAt(firstname.length - 1);
        if (lastChar === "'" || lastChar === "-") {
          return { lastCharInvalid: true };
        }

        // Check no more than 1 dashes
        if (firstname?.search("--") >= 0) {
          return { moreThanOneDash: true };
        }
      }
      return null;
    }

    return null;
  };

  public checkConsecutiveApostrophes(s: string): { [key: string]: true } {
    if (s?.search("''") >= 0) {
      return { tooManyApostrophes: true };
    }
  }

  public checkConsecutiveSpaces(s: string): { [key: string]: true } {
    if (s?.search("  ") >= 0) {
      return { tooManySpaces: true };
    }
  }

  public checkFirstLetter(s: string): { [key: string]: true } {
    const firstChar: string = s?.charAt(0);
    if (firstChar === " " || firstChar === "-") {
      return { firstCharInvalid: true };
    }
  }

  private makeBirthplaceRequired(): void {
    // townServerSideSearch is undefined when birthdate is not set. in that case, form submission will be stopped by patientForm validation
    if (!this.townServerSideSearch) {
      return;
    }
    this.townServerSideSearch.townServerSideFilteringCtrl.addValidators(Validators.required);
    this.townServerSideSearch.townServerSideFilteringCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.townServerSideSearch.townServerSideFilteringCtrl.markAsDirty();
    this.townServerSideSearch.townServerSideFilteringCtrl.markAsTouched();
    this.birthplaceRequired = true; // necessary to add the required attribute on the actual input for accessibility and to show the * on the field
  }

  public preventCharacter(event: KeyboardEvent, characters: PREVENTCHARACTER[], formControlName?: string): void {
    // we need this timeout because the replacement must be after the change of the input value
    setTimeout(() => {
      const value = PreventCharacter.preventMultiple(event, characters);
      this.patientForm.get(formControlName).setValue(value, { emitEvent: false });
    }, 0);
  }

  public onRelatedCheckboxChange(): void {
    this.addRelated = !this.addRelated;
    this.patientRegistrationService.onNeedReferentChanged$.next(this.addRelated);
    this.patientForm.get("mail").setValidators(this.addRelated ? [Validators.email] : [Validators.required, Validators.email]);
    this.patientForm
      .get("phone")
      .setValidators(
        this.addRelated
          ? [this.phoneValidator, Validators.pattern("[0-9+]*")]
          : [Validators.required, this.phoneValidator, Validators.pattern("[0-9+]*")]
      );
  }

  public cancel(): void {
    this.init();
  }
}
